import { api } from './client';

export interface StockItem {
  code: string; name: string; market: string; industry: string;
  totalCap: number; floatCap: number; price: number; changePct: number;
  volume: number; turnover: number; pe: number; pb: number; turnoverRate: number;
  reason?: string;
  matchScore?: number;
}

export interface IndustryStat { industry: string; count: number; }

export interface StockParams {
  q?: string; market?: string; industry?: string;
  minCap?: number; maxCap?: number;
  orderBy?: string; order?: string;
  page?: number; pageSize?: number;
  strategy?: string;  // value | growth | trend | mixed (v6.2)
}

export const getStocks = (params: StockParams = {}) => {
  const p = new URLSearchParams();
  if (params.q) p.set('q', params.q);
  if (params.market) p.set('market', params.market);
  if (params.industry) p.set('industry', params.industry);
  if (params.minCap != null) p.set('min_cap', String(params.minCap));
  if (params.maxCap != null) p.set('max_cap', String(params.maxCap));
  if (params.orderBy) p.set('order_by', params.orderBy);
  if (params.order) p.set('order', params.order);
  if (params.page != null) p.set('page', String(params.page));
  if (params.pageSize != null) p.set('page_size', String(params.pageSize));
  if (params.strategy) p.set('strategy', params.strategy);
  return api.get<{ stocks: StockItem[]; total: number; page: number }>(`/market/stocks?${p.toString()}`);
};

export const getIndustries = () =>
  api.get<{ industries: IndustryStat[] }>('/market/industries');

export const getRecommendStocks = (sessionId: number, watchlist?: string) =>
  api.get<{ stocks: StockItem[] }>(
    `/market/stocks/recommend?session_id=${sessionId}${watchlist ? `&watchlist=${encodeURIComponent(watchlist)}` : ''}`,
  );

export interface IndexSnapshot {
  code: string; name: string; price: number;
  prevClose: number; changePct: number; changeAmt: number;
  volume: number; turnover: number; high: number; low: number;
  open: number; quoteTime: number; updatedAt: number;
}

export interface QuoteSnapshot {
  code: string; name: string; price: number; prevClose: number;
  changePct: number; changeAmt: number; volume: number; turnover: number;
  high: number; low: number; open: number;
  pe: number; pb: number; turnoverRate: number;
  quoteTime: number; updatedAt: number;
}

export interface KlineDaily {
  code: string; tradeDate: string;
  open: number; close: number; high: number; low: number;
  volume: number; turnover: number; changePct: number;
}

export interface MarketNews {
  id: number; source: string; title: string; url: string;
  summary: string; tickers: string | string[]; sentiment: string;
  // Populated by the backend news analysis worker. importance is one of
  // critical|high|normal|low; '' means not yet analysed. aiAnalysis is the
  // LLM-generated impact analysis; '' means not yet analysed.
  importance: string; aiAnalysis: string;
  publishedAt: number; createdAt: number;
}

export const getIndices = (codes?: string[]) => {
  const q = codes ? `?codes=${codes.join(',')}` : '';
  return api.get<{ indices: IndexSnapshot[] }>(`/market/indices${q}`);
};

export const getQuote = (code: string) =>
  api.get<{ quote: QuoteSnapshot; ticker?: { totalCap: number; floatCap: number; industry: string } }>(`/market/tickers/${code}`);

export const getKline = (code: string, start?: string, end?: string, granularity?: string) => {
  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  if (granularity) params.set('granularity', granularity);
  const q = params.toString();
  return api.get<{ code: string; granularity: string; candles: any[] }>(`/market/kline/${code}${q ? `?${q}` : ''}`);
};

export const getIndexKline = (code: string, start?: string, end?: string) => {
  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  const q = params.toString();
  return api.get<{ code: string; granularity: string; candles: KlineDaily[] }>(`/market/index-kline/${code}${q ? `?${q}` : ''}`);
};

// before is a publishedAt cursor for infinite scroll — pass the oldest
// loaded item's publishedAt to fetch the next (older) page. 0/undefined
// returns the latest page.
export const getNews = (ticker?: string, limit = 30, before?: number, since?: number) => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (ticker) params.set('ticker', ticker);
  if (before) params.set('before', String(before));
  if (since) params.set('since', String(since));
  return api.get<{ news: MarketNews[] }>(`/market/news?${params}`);
};

export const getMarketHealth = () =>
  api.get<{ freshnessSecs: number; trading: boolean }>('/market/health');

export const getStockMetrics = (code: string) =>
  api.get<{ metrics: { roe: number; netProfitGrowth: number; grossMargin: number; pePercentile60d: number } | null; available: boolean }>(`/market/stock-metrics?code=${encodeURIComponent(code)}`);

export interface SuspendedStock {
  code: string; name: string;
  suspendFrom: string; suspendUntil: string; reason: string;
}

export const getSuspended = () =>
  api.get<{ suspended: SuspendedStock[]; count: number }>('/market/suspended');

export interface CalendarToday {
  date: string;       // YYYY-MM-DD
  weekday: number;    // 0=Sun..6=Sat
  isTrading: boolean;
  checkedAt: number;
}

export const getCalendarToday = () =>
  api.get<CalendarToday>('/market/calendar/today');

// ── 自选股 (sim_watchlist) ──

export interface WatchlistEntry {
  code: string;
  name: string;
  entryPrice: number;
  currentPrice: number;
  changePct: number;
  industry: string;
  addedAt: number;
  todayChangePct: number;
}

export const getWatchlist = () =>
  api.get<{ watchlist: WatchlistEntry[]; count: number }>('/sim/watchlist');

export const addToWatchlist = (ticker: string) =>
  api.post<{ ok: boolean }>('/sim/watchlist', { ticker });

export const removeFromWatchlist = (code: string) =>
  api.delete<{ ok: boolean }>(`/sim/watchlist/${encodeURIComponent(code)}`);

export const importWatchlist = (codes: string[]) =>
  api.post<{ ok: boolean }>('/sim/watchlist/import', { codes });
